import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/**
 * 🔑 Reset Password landing page.
 *
 * Reached via the email link sent by /api/auth/forgot-password.
 * Reads the one-shot reset token from the `?token=` query param, asks the
 * user for a new password (min 8 chars per backend rule), and posts
 * to /api/auth/reset-password. On success, deep-links the user back to
 * `/?signin=1` so the header can pop the AuthModal open in sign-in mode.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  // ⚠️ Defensive guard: if the user lands here without a token (manual URL
  // typo, broken email rendering), render a friendly error rather than
  // a half-broken form.
  if (!token) {
    return (
      <div
        data-testid="reset-password-no-token"
        className="min-h-screen bg-black flex items-center justify-center p-4"
      >
        <div className="bg-[#0F0F0F] border-2 border-[#DC2626] max-w-md w-full p-6 text-center">
          <AlertTriangle className="text-[#EF4444] mx-auto mb-3" size={40} />
          <h2 className="font-display text-2xl text-white mb-2">Invalid Reset Link</h2>
          <p className="text-sm text-neutral-400 mb-5">
            This URL is missing a reset token. Use the link from the email we sent you, or
            request a new password reset from the sign-in screen.
          </p>
          <Link
            to="/"
            data-testid="reset-password-home-link"
            className="inline-block btn-omni btn-red"
          >
            ← Back to OmniMuscle
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen bg-black flex items-center justify-center p-4"
    >
      <div className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
          Account Recovery
        </div>
        <h2 className="font-display text-3xl text-white mb-1 leading-tight">
          {done ? "Password Updated" : "Set a New Password"}
        </h2>
        <p className="text-neutral-400 text-xs mb-5">
          {done
            ? "You can sign in with your new password now."
            : "Pick a strong password — minimum 8 characters."}
        </p>

        {done ? (
          <div data-testid="reset-password-success" className="text-center">
            <CheckCircle2 className="text-[#D4AF37] mx-auto mb-3" size={48} />
            <p className="text-sm text-neutral-300 mb-5">
              Your password has been reset successfully. Head back to the sign-in screen to
              continue your journey.
            </p>
            <button
              data-testid="reset-password-go-signin"
              onClick={() => {
                navigate("/");
                // Fire the global open-auth event so the Header's AuthModal
                // pops open in sign-in mode after we land on home.
                setTimeout(() => {
                  window.dispatchEvent(new Event("omni:open-auth"));
                }, 50);
              }}
              className="btn-omni btn-red w-full"
            >
              <Sparkles size={14} /> Go to Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
                New Password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  data-testid="reset-password-new"
                  type="password"
                  className="input-omni pl-9"
                  placeholder="Min 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  data-testid="reset-password-confirm"
                  type="password"
                  className="input-omni pl-9"
                  placeholder="Re-enter password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="reset-password-error"
                className="text-sm text-[#EF4444] border border-[#DC2626] bg-[#DC2626]/10 px-3 py-2"
              >
                {err}
              </div>
            )}

            <button
              data-testid="reset-password-submit"
              type="submit"
              disabled={busy}
              className="btn-omni btn-red w-full disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Working…
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Update Password
                </>
              )}
            </button>

            <Link
              to="/"
              data-testid="reset-password-cancel"
              className="block text-xs text-neutral-400 hover:text-[#D4AF37] mt-4 w-full text-center"
            >
              ← Cancel and return home
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
